import React, { useState } from "react"
import PropTypes from "prop-types"
import { Typography, Space, Card, Result, Button, Spin } from "antd"

import api from "@api"
import { useAppContext } from "@components/AppContext"
import { W1, W2, isMobile } from "@components/constants"
import { getUsDateFromIsoDate } from "@helpers/formats"

const { Text } = Typography

const LABEL_TITLE = "Thank You!"
const LABEL_CONTINUE = "Continue to Complete Investment"
const LABEL_SUCCESS = "Thank you for submitting a soft commitment, your spot has been saved! The next steps are to sign the PPM and initiate your funding."
const LABEL_FINISH_LATER = "Complete Submission Later"
const LABEL_PPM_DEADLINE = "PPM Deadline"
const LABEL_FUND_DEADLINE = "Funding Deadline"
const LABEL_FINISH_LATER_TEXT = "We have sent you an email containing a link so you can complete your submission at a later time."

const postponeCommitmentOperation = api.get("investments/PostponeCommitment")


const Success = ({
  email,
  hasUser,
  projectId,
  commitmentId,
  signDeadlineDate,
  fundDeadlineDate,
}) => {
  const { request } = useAppContext()

  const [ isLoading, setIsLoading ] = useState(false)
  const [ showFinishLater, setShowFinishLater ] = useState(false)

  const encodedEmail = encodeURIComponent(email)
  const usSignDeadline = getUsDateFromIsoDate(signDeadlineDate)
  const usFundDeadline = getUsDateFromIsoDate(fundDeadlineDate)

  const successMessage = () => {
    let message = LABEL_SUCCESS

    if (signDeadlineDate) {
      message += `\n ${LABEL_PPM_DEADLINE}: ${usSignDeadline}`
    }

    if (fundDeadlineDate) {
      message += `\n ${LABEL_FUND_DEADLINE}: ${usFundDeadline}`
    }

    return (
      <Text type="secondary">
        {message}
      </Text>
    )
  }

  const signInPath = `/investments/${projectId}/?action=signIn&email=${encodedEmail}`

  const signUpPath = `/investments/${projectId}/?action=signUp&email=${encodedEmail}`

  const onSignIn = () =>
    window.location = signInPath

  const onSignUp = () =>
    window.location = signUpPath

  const onFinishLater = () => {
    setIsLoading(true)

    return request(postponeCommitmentOperation.id, { id: commitmentId })
      .then(() => setShowFinishLater(true))
      .then(() => setIsLoading(false))
  }

  const buttonProps = {
    size: "large",
    type: "primary",
    shape: "round",
    style: { width: "100%" }
  }

  const loginActionButton = hasUser
    ? (
        <Button key='signIn' {...buttonProps} onClick={() => onSignIn()}>
          {LABEL_CONTINUE}
        </Button>
      )
    : (
        <Button key='signUp' {...buttonProps} onClick={() => onSignUp()}>
          {LABEL_CONTINUE}
        </Button>
      )

  const children = showFinishLater
    ? (
        <Result
          title={LABEL_TITLE}
          style={{ paddingBottom: W2 }}
          status="success"
          subTitle={LABEL_FINISH_LATER_TEXT}
        />
      )
    : (
        <Result
          title={LABEL_TITLE}
          style={{ paddingBottom: W2 }}
          status="success"
          subTitle={
            <Space
              size={0}
              style={{ whiteSpace: "pre-wrap" }}
              direction="vertical"
            >
              {successMessage()}
            </Space>
          }
          extra={
            <Spin spinning={isLoading}>
              <Space
                style={{ width: "100%" }}
                direction="vertical"
              >
                {loginActionButton}

                <div style={{ marginTop: W1 }}>
                  <Typography.Link
                    style={{ width: "100%" }}
                    onClick={onFinishLater}
                  >
                    {LABEL_FINISH_LATER}
                  </Typography.Link>
                </div>
              </Space>
            </Spin>
          }
        />
      )

  if (isMobile) {
    return children
  }

  return (
    <Card>
      {children}
    </Card>
  )
}

Success.propTypes = {
  email: PropTypes.string.isRequired,
  hasUser: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  commitmentId: PropTypes.string.isRequired,
  signDeadlineDate: PropTypes.string,
  fundDeadlineDate: PropTypes.string,
}

Success.defaultProps = {
  hasUser: false,
  fundDeadlineDate: undefined,
  signDeadlineDate: undefined,
}

export default Success
